import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLink, FaExternalLinkAlt, FaChartBar, FaEdit, FaTrash, FaCopy, FaCheck, FaEye, FaProjectDiagram, FaCalendarAlt, FaMousePointer, FaFolder, FaBars, FaTimes } from 'react-icons/fa';
import { getBaseUrl } from '../utils/tokenUtils';

const ShortLinkList = ({ shortLinks, projects, onEdit, onDelete, onRefresh, onViewAnalytics, onShowToast }) => {
  const [copied, setCopied] = React.useState({});
  const [expandedLinks, setExpandedLinks] = React.useState(new Set());
  const navigate = useNavigate();

  const getStatusColor = (isActive) => {
    return isActive 
      ? 'text-green-400 bg-green-900/20 border-green-500/30'
      : 'text-red-400 bg-red-900/20 border-red-500/30';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const getStatusDotColor = (isActive) => {
    return isActive ? 'bg-green-400' : 'bg-gray-400';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(dateString);
  };

  const copyToClipboard = async (text, shortLinkId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [shortLinkId]: true }));
      
      // Show toast notification
      if (onShowToast) {
        onShowToast('Link copied to clipboard!', 'success');
      }
      
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [shortLinkId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      
      // Show error toast
      if (onShowToast) {
        onShowToast('Failed to copy link', 'error');
      }
    }
  };

  const getProjectName = (projectId) => {
    // Handle both string ID and populated object
    let project;
    if (typeof projectId === 'string') {
      project = projects.find(p => p._id === projectId);
    } else if (projectId && projectId._id) {
      // If projectId is already populated object
      project = projectId;
    }
    
    return project ? project.name : 'Unknown Project';
  };

  // Toggle mobile menu for a specific short link
  const toggleLinkMenu = (linkId) => {
    setExpandedLinks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(linkId)) {
        newSet.delete(linkId);
      } else {
        newSet.add(linkId);
      }
      return newSet;
    });
  };

  if (!shortLinks || shortLinks.length === 0) {
    return (
      <div className="text-center py-12">
        <FaLink className="h-16 w-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-300 mb-2">No Short Links</h3>
        <p className="text-gray-400">Short links will appear here when you create them.</p>
      </div>
    );
  }

  const handleItemClick = (shortLink) => {
    if (shortLink && shortLink._id) {
      navigate(`/dashboard/shortlinks/${shortLink._id}`);
    }
  };

  return (
    <div className="space-y-4">
      {shortLinks.map((shortLink) => {
        const isExpanded = expandedLinks.has(shortLink._id);
        return (
          <div 
            key={shortLink?._id} 
            className="bg-black rounded-lg border border-gray-800 hover:border-gray-700 transition-all duration-200 cursor-pointer"
            onClick={() => handleItemClick(shortLink)}
          >
            {/* Compact Row */}
            <div className="p-4 lg:p-6">
              <div className="flex items-start lg:items-center justify-between">
                {/* Left Side - Main Info */}
                <div className="flex items-center space-x-3 lg:space-x-4 flex-1 min-w-0 pr-2">
                  {/* Project Icon with Status Dot */}
                  <div className="flex-shrink-0 w-8 h-8 lg:w-12 lg:h-12 relative">
                    <div className="w-8 h-8 lg:w-12 lg:h-12 bg-gray-800 rounded-xl flex items-center justify-center relative">
                      {(() => {
                        // Get project info
                        let project;
                        if (typeof shortLink?.projectId === 'string') {
                          project = projects.find(p => p._id === shortLink.projectId);
                        } else if (shortLink?.projectId && shortLink.projectId._id) {
                          project = shortLink.projectId;
                        }
                        
                        // Show project logo if available, otherwise default folder icon
                        if (project && project.logo) {
                          return (
                            <>
                              <img 
                                src={project.logo.startsWith('http') ? project.logo : `${getBaseUrl()}${project.logo}`}
                                alt={project.name}
                                className="w-8 h-8 lg:w-12 lg:h-12 rounded-xl object-cover border border-gray-600"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                              <div 
                                className="w-8 h-8 lg:w-12 lg:h-12 bg-gray-800 rounded-xl flex items-center justify-center absolute inset-0"
                                style={{ display: 'none' }}
                              >
                                <FaFolder className="w-4 h-4 lg:w-6 lg:h-6 text-gray-300" />
                              </div>
                            </>
                          );
                        } else {
                          return <FaFolder className="w-4 h-4 lg:w-6 lg:h-6 text-gray-300" />;
                        }
                      })()}
                    </div>
                    {/* Status Indicator - Top Right of Icon */}
                    <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-gray-900 ${getStatusDotColor(shortLink?.isActive)}`}></div>
                  </div>

                  {/* Short Link Info */}
                  <div className="flex flex-col space-y-1 flex-1 min-w-0">
                    {/* Title */}
                    {shortLink?.title && (
                      <div className="text-sm text-gray-300 text-left truncate">
                        {shortLink.title}
                      </div>
                    )}
                    
                    {/* Short Link URL */}
                    <div className="text-sm text-left">
                      <span className="text-gray-400 text-xs lg:text-sm">{getBaseUrl()}/s/</span>
                      <span className="text-white font-bold text-xs lg:text-sm">{shortLink?.shortCode || 'N/A'}</span>
                    </div>
                    
                    {/* Project and Stats */}
                    <div className="flex flex-wrap items-center gap-2 lg:gap-3 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <FaProjectDiagram className="h-3 w-3" />
                        <span className="truncate">{getProjectName(shortLink?.projectId)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FaMousePointer className="h-3 w-3" />
                        <span>{shortLink?.analytics?.totalClicks || 0} clicks</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FaCalendarAlt className="h-3 w-3" />
                        <span>{getTimeAgo(shortLink?.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Actions */}
                <div className="hidden lg:flex items-center space-x-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {/* Copy Button */}
                  <button
                    onClick={() => copyToClipboard(`${getBaseUrl()}/s/${shortLink?.shortCode}`, shortLink?._id)}
                    className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                    title="Copy short link"
                  >
                    {copied[shortLink?._id] ? (
                      <FaCheck className="mr-2 text-green-400" />
                    ) : (
                      <FaCopy className="mr-2" />
                    )}
                    {copied[shortLink?._id] ? 'Copied' : 'Copy'}
                  </button>

                  {/* View Analytics Button */}
                  <button
                    onClick={() => navigate(`/dashboard/shortlinks/${shortLink._id}`)}
                    className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                    title="View analytics"
                  >
                    <FaChartBar className="mr-2" />
                    Analytics
                  </button>
                </div>

                {/* Mobile hamburger menu */}
                <div className="lg:hidden flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => toggleLinkMenu(shortLink._id)}
                    className="p-2 text-white hover:text-gray-300 transition-colors bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600"
                  >
                    {isExpanded ? (
                      <FaTimes className="h-4 w-4" />
                    ) : (
                      <FaBars className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Mobile expanded menu */}
              {isExpanded && (
                <div className="lg:hidden mt-4 pt-4 border-t border-gray-600 bg-gray-800 rounded-lg p-3">
                  <div className="grid grid-cols-2 gap-2 lg:gap-3">
                    <button
                      onClick={() => {
                        copyToClipboard(`${getBaseUrl()}/s/${shortLink?.shortCode}`, shortLink?._id);
                        toggleLinkMenu(shortLink._id);
                      }}
                      className="px-2 lg:px-3 py-2 bg-gray-700 text-white text-xs font-medium rounded hover:bg-gray-600 transition-colors flex items-center justify-center"
                    >
                      {copied[shortLink?._id] ? (
                        <FaCheck className="mr-1 h-3 w-3 lg:h-4 lg:w-4 text-green-400" />
                      ) : (
                        <FaCopy className="mr-1 h-3 w-3 lg:h-4 lg:w-4" />
                      )}
                      {copied[shortLink?._id] ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/dashboard/shortlinks/${shortLink._id}`);
                        toggleLinkMenu(shortLink._id);
                      }}
                      className="px-2 lg:px-3 py-2 bg-gray-700 text-white text-xs font-medium rounded hover:bg-gray-600 transition-colors flex items-center justify-center"
                    >
                      <FaChartBar className="mr-1 h-3 w-3 lg:h-4 lg:w-4" />
                      Analytics
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ShortLinkList;